import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Form, Formik, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FormField } from "@/components/common/form-field";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import { TextareaField } from "@/features/admin/components/textarea-field";
import {
  createSystemEvent,
  deleteSystemEvent,
  listSystemEvents,
  reorderSystemEvents,
  updateSystemEvent,
} from "@/features/admin/api";
import type { SystemEvent, SystemEventInput } from "@/features/admin/types";
import { applyApiErrors } from "@/lib/form-errors";

export type EventFieldKey =
  | "eventDate"
  | "eventTime"
  | "title"
  | "href"
  | "detail"
  | "badge"
  | "newTab"
  | "password";

export interface EventColumn {
  header: string;
  cell: (e: SystemEvent) => ReactNode;
}

interface SystemEventListPanelProps {
  title: string;
  description?: string;
  addLabel: string;
  resource: string;
  selector?: { param: string; value: string };
  fields: EventFieldKey[];
  columns: EventColumn[];
}

interface FormValues {
  title: string;
  href: string;
  detail: string;
  eventDate: string;
  eventTime: string;
  badge: string;
  newTab: boolean;
  passwordProtected: boolean;
  openPassword: string;
}

function toInput(values: FormValues, fields: EventFieldKey[]): SystemEventInput {
  const input: SystemEventInput = { title: values.title };
  if (fields.includes("href")) input.href = values.href;
  if (fields.includes("detail")) input.detail = values.detail;
  if (fields.includes("eventDate")) input.eventDate = values.eventDate || null;
  if (fields.includes("eventTime")) input.eventTime = values.eventTime;
  if (fields.includes("badge")) input.badge = values.badge === "NONE" ? "" : values.badge;
  if (fields.includes("newTab")) input.newTab = values.newTab;
  if (fields.includes("password")) {
    input.passwordProtected = values.passwordProtected;
    input.openPassword = values.passwordProtected ? values.openPassword : "";
  }
  return input;
}

/**
 * Reusable ordered-CRUD panel over a single `SystemEvent` list (events /
 * resources / toolbox section / video section). Renders a table with add / edit
 * (modal Formik form) / delete (confirm) / accessible up-down reorder.
 */
export function SystemEventListPanel({
  title,
  description,
  addLabel,
  resource,
  selector,
  fields,
  columns,
}: SystemEventListPanelProps) {
  const [items, setItems] = useState<SystemEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<{ item: SystemEvent | null } | null>(null);
  const [deleting, setDeleting] = useState<SystemEvent | null>(null);
  const [reordering, setReordering] = useState(false);

  const selectorKey = selector ? `${selector.param}=${selector.value}` : resource;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listSystemEvents(resource, selector));
    } catch {
      toast.error("Couldn't load the list.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= items.length || reordering) return;
    const ids = items.map((i) => i.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    setReordering(true);
    try {
      setItems(await reorderSystemEvents(resource, ids, selector));
    } catch {
      toast.error("Couldn't reorder.");
    } finally {
      setReordering(false);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteSystemEvent(resource, deleting.id);
      setDeleting(null);
      void refresh();
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  }

  async function handleSubmit(values: FormValues, helpers: FormikHelpers<FormValues>) {
    const input = toInput(values, fields);
    try {
      if (dialog?.item) await updateSystemEvent(resource, dialog.item.id, input);
      else await createSystemEvent(resource, input, selector);
      setDialog(null);
      void refresh();
      toast.success(dialog?.item ? "Saved." : "Added.");
    } catch (err) {
      toast.error(applyApiErrors(err, helpers, { fallback: "Couldn't save." }));
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <CardHeader className="flex flex-row items-start justify-between gap-4 p-4">
        <div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setDialog({ item: null })}>
          <Plus className="size-4" /> {addLabel}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <p className="text-muted-foreground p-4 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground p-4 text-sm">Nothing here yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c.header}>{c.header}</TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id}>
                    {columns.map((c) => (
                      <TableCell key={c.header}>{c.cell(item)}</TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={index === 0 || reordering}
                          onClick={() => void move(index, -1)}
                          aria-label="Move up"
                        >
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={index === items.length - 1 || reordering}
                          onClick={() => void move(index, 1)}
                          aria-label="Move down"
                        >
                          <ArrowDown className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDialog({ item })}
                          aria-label="Edit"
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleting(item)}
                          aria-label="Delete"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialog?.item ? "Edit" : addLabel}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <Formik<FormValues>
              initialValues={{
                title: dialog.item?.title ?? "",
                href: dialog.item?.href ?? "",
                detail: dialog.item?.detail ?? "",
                eventDate: dialog.item?.eventDate ?? "",
                eventTime: dialog.item?.eventTime ?? "",
                badge: dialog.item?.badge || "NONE",
                newTab: dialog.item?.newTab ?? false,
                passwordProtected: dialog.item?.passwordProtected ?? false,
                openPassword: dialog.item?.openPassword ?? "",
              }}
              validationSchema={Yup.object({
                title: Yup.string().trim().required("Title is required").max(200),
                href: Yup.string().max(255),
              })}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values }) => (
                <Form className="grid gap-4">
                  <FormField name="title" label="Title" placeholder="Title" />
                  {fields.includes("eventDate") && (
                    <FormField name="eventDate" label="Date" type="date" />
                  )}
                  {fields.includes("eventTime") && (
                    <FormField name="eventTime" label="Time" placeholder="e.g. 2:00 PM ET" />
                  )}
                  {fields.includes("href") && (
                    <FormField name="href" label="Link (URL)" placeholder="https://…" />
                  )}
                  {fields.includes("badge") && (
                    <SelectField
                      name="badge"
                      label="Badge"
                      options={[
                        { value: "NONE", label: "None" },
                        { value: "NEW!", label: "NEW!" },
                      ]}
                    />
                  )}
                  {fields.includes("detail") && (
                    <TextareaField name="detail" label="Detail" placeholder="Description" />
                  )}
                  {fields.includes("newTab") && (
                    <CheckboxField name="newTab" label="Open in a new tab" />
                  )}
                  {fields.includes("password") && (
                    <>
                      <CheckboxField
                        name="passwordProtected"
                        label="Require a password to open"
                      />
                      {values.passwordProtected && (
                        <FormField
                          name="openPassword"
                          label="Shared password"
                          placeholder="Password"
                        />
                      )}
                    </>
                  )}
                  <DialogFooter>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Saving…" : dialog.item ? "Save" : "Add"}
                    </Button>
                  </DialogFooter>
                </Form>
              )}
            </Formik>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
